import { User, UserGroup, Product, PermissionLevel } from './types';

export const getGroupMembers = (group: UserGroup, users: User[]): User[] => {
  return users.filter(u => (group.userIds || []).includes(u.id));
};

export const getGroupUserCount = (group: UserGroup): number => {
  return (group.userIds || []).length;
};

export const formatProductKey = (product: string): string => {
  return product.toLowerCase().replace(/\s+/g, '_').replace(/[\/]/g, '_');
};

export const formatProductName = (productKey: string): string => {
  return productKey
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

/**
 * Find product by name or key
 */
export const findProduct = (products: Product[], nameOrKey: string): Product | undefined => {
  const lowerNameOrKey = nameOrKey.toLowerCase();
  return products.find(
    p => p.product_name.toLowerCase() === lowerNameOrKey || 
         p.product_key.toLowerCase() === lowerNameOrKey
  );
};

/**
 * Find permission level by name
 */
export const findPermissionLevel = (levels: PermissionLevel[], levelName: string): PermissionLevel | undefined => {
  return levels.find(l => l.level_name.toLowerCase() === levelName.toLowerCase());
};

/**
 * Convert product name to API-compatible permission array
 */
export const createPermissionPayload = (
  productNameOrKey: string,
  levelName: string,
  products: Product[]
): { productId: number; levelName: string } | null => {
  const product = findProduct(products, productNameOrKey);
  if (!product) return null;
  return {
    productId: product.product_id,
    levelName,
  };
};

/**
 * Merge existing permissions with a new permission
 */
export const mergePermissions = (
  existingPermissions: Record<string, string>,
  productKey: string,
  levelName: string
): Record<string, string> => {
  return {
    ...existingPermissions,
    [productKey]: levelName,
  };
};

/**
 * Remove a permission from the permissions object
 */
export const removePermission = (
  permissions: Record<string, string>,
  productKey: string
): Record<string, string> => {
  const { [productKey]: _, ...rest } = permissions;
  return rest;
};

/**
 * Convert legacy permissions Record to API payload format
 */
export const permissionsRecordToPayload = (
  permissions: Record<string, string>,
  products: Product[]
): Array<{ productId: number; levelName: string }> => {
  return Object.entries(permissions)
    .map(([key, levelName]) => {
      const product = findProduct(products, key);
      if (!product) return null;
      return {
        productId: product.product_id,
        levelName,
      };
    })
    .filter((p): p is { productId: number; levelName: string } => p !== null);
};
